import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Phone, FileText, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AppHeader from "@/components/AppHeader";
import { Pager, paginate } from "@/components/Pager";
import QuoteRow from "@/components/history/QuoteRow";
import StatusFilter from "@/components/history/StatusFilter";
import { statusOf, isOngoing, sortOngoingFirst } from "@/lib/quoteStatus";
import { openReadyWhatsApp } from "@/lib/collection";
import { formatMoney } from "@/lib/pricing";
import { useShopMeta } from "@/hooks/useCurrencySymbol";
import { useCollectionSend } from "@/hooks/useCollectionSend";
import { useQuotePrinter } from "@/hooks/useQuotePrinter";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

function CustomerFilterBanner({ customer, onClear }) {
  if (!customer) return null;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2.5" data-testid="quotes-customer-filter">
      <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Filtered</span>
      <span className="font-semibold">{customer.name}</span>
      <span className="text-slate-400 font-mono-num text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> {customer.phone}</span>
      <button onClick={onClear} data-testid="quotes-clear-filter-btn" className="ml-auto text-slate-400 hover:text-slate-200 flex items-center gap-1 text-xs"><X className="w-3.5 h-3.5" /> Clear</button>
    </div>
  );
}

export default function Quotes() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const customerId = params.get("customer");
  const [quotes, setQuotes] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [q, setQ] = useState(() => params.get("q") || "");
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const shop = useShopMeta();
  const { promptSend, element: collectionPrompt } = useCollectionSend(shop);
  const sym = shop.sym;
  const { exportPdf, sharePdf, print, portal } = useQuotePrinter();

  useEffect(() => {
    if (!customerId) { setCustomer(null); return; }
    axios.get(`${API}/customers/${customerId}`).then(({ data }) => setCustomer(data)).catch(() => setCustomer(null));
  }, [customerId]);

  const load = useCallback(async () => {
    const p = {};
    if (customerId) p.customer_id = customerId;
    if (q.trim()) p.q = q.trim();
    const { data } = await axios.get(`${API}/quotes`, { params: p });
    setQuotes(data);
    setLoading(false);
  }, [customerId, q]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await axios.delete(`${API}/quotes/${id}`);
    toast.success("Quote deleted");
    load();
  };

  // Approving a quote creates its invoice straight away (monthly-billing customers are consolidated on the Invoices page instead)
  const busy = React.useRef(new Set());
  const setStatus = async (id, status) => {
    if (busy.current.has(id)) return; // ignore double taps while a status/invoice request is in flight
    busy.current.add(id);
    try { await applyStatus(id, status); } finally { busy.current.delete(id); }
  };
  const applyStatus = async (id, status) => {
    let data;
    try { ({ data } = await axios.patch(`${API}/quotes/${id}/status`, { status })); }
    catch (e) { return toast.error("Status not changed", { description: e?.response?.data?.detail }); }
    let next = { ...data, monthly_billing: quotes.find((x) => x.id === id)?.monthly_billing };
    if (status === "approved" && !next.invoice_no && !next.monthly_billing) {
      try {
        const { data: inv } = await axios.post(`${API}/quotes/${id}/invoice`, {});
        next = { ...next, invoice_id: inv.id, invoice_no: inv.invoice_no };
        toast.success(`Approved · invoice ${inv.invoice_no} created`, { description: `Due ${inv.due_date} · 已确认并开票`, action: { label: "Invoices", onClick: () => navigate("/invoices") } });
      } catch (e) {
        toast.error("Approved, but invoice not created", { description: e?.response?.data?.detail });
      }
    } else if (status === "draft") {
      const had = quotes.find((x) => x.id === id)?.invoice_no;
      toast.success("Back to draft — editable again · 已改回草稿", { description: had ? `Invoice ${had} removed` : undefined });
    } else {
      toast.success(`Marked as ${status}`);
    }
    setQuotes((list) => sortOngoingFirst(list.map((x) => (x.id === id ? next : x))));
  };

  // Vehicle collected → job no longer "Ongoing" (drops out of the red priority group)
  const setOngoing = async (id, ongoing, { notify = false } = {}) => {
    const qt = quotes.find((x) => x.id === id);
    const sent = !ongoing && notify && qt ? openReadyWhatsApp(qt, shop) : false; // WhatsApp only when asked
    try {
      const { data } = await axios.patch(`${API}/quotes/${id}/ongoing`, { ongoing });
      setQuotes((list) => sortOngoingFirst(list.map((x) => (x.id === id ? { ...x, ongoing: data.ongoing, invoice_id: data.invoice_id, invoice_no: data.invoice_no } : x))));
      const wa = !ongoing && notify ? (sent ? "WhatsApp opened for the customer" : "No phone on this quote — customer not messaged") : null;
      const invMsg = data.invoice_created ? `Invoice ${data.invoice_created} created · 已开票` : null;
      toast.success(ongoing ? "Marked as ongoing · 进行中" : "Vehicle collected · 已交车", { description: [invMsg, wa].filter(Boolean).join(" · ") || undefined, action: invMsg ? { label: "Invoices", onClick: () => navigate("/invoices") } : undefined });
      if (!ongoing) promptSend(data);
    } catch (e) { toast.error("Could not update", { description: e?.response?.data?.detail }); }
  };

  const createInvoice = async (qt) => {
    if (busy.current.has(qt.id)) return;
    busy.current.add(qt.id);
    try {
      const { data } = await axios.post(`${API}/quotes/${qt.id}/invoice`, {});
      setQuotes((list) => list.map((x) => (x.id === qt.id ? { ...x, invoice_id: data.id, invoice_no: data.invoice_no } : x)));
      toast.success(`Invoice ${data.invoice_no} created`, { description: `Due ${data.due_date} · open Invoices to send it` });
    } catch (e) {
      toast.error("Could not create invoice", { description: e?.response?.data?.detail });
    } finally { busy.current.delete(qt.id); }
  };

  const grand = quotes.reduce((s, x) => s + Number(x.total || 0), 0);
  const filtered = statusFilter === "ongoing" ? quotes.filter(isOngoing) : statusFilter ? quotes.filter((x) => statusOf(x).key === statusFilter) : quotes;
  const { pageCount, current, visible } = paginate(filtered, page);
  const changePage = (p) => { setPage(p); setOpen(null); };

  return (
    <div className="min-h-screen workshop-bg text-slate-100">
      <AppHeader right={
        <Button onClick={() => navigate(customerId ? `/quote?customer=${customerId}` : "/quote")} data-testid="history-new-quote-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
          <FileText className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">New Quote</span>
        </Button>
      } />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Activity log · 活动记录</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">Activity log <span className="font-zh text-amber-300/70 text-2xl">活动记录</span></h2>
            <p className="text-sm text-slate-400 mt-1" data-testid="quotes-summary">{quotes.length} quotes · <span className="font-mono-num text-amber-300">{formatMoney(sym, grand)}</span> total</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="quotes-search-input" placeholder="Search quote no, customer, plate…" className="bg-slate-900/60 border-slate-700 pl-9 h-11" />
          </div>
        </div>

        <CustomerFilterBanner customer={customer} onClear={() => setParams({})} />
        {quotes.length > 0 && <StatusFilter quotes={quotes} value={statusFilter} onChange={(v) => { setStatusFilter(v); changePage(1); }} />}

        {loading ? (
          <div className="text-slate-500 py-16 text-center">Loading quotes…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-12 text-center" data-testid="quotes-empty-state">
            <FileText className="w-10 h-10 text-slate-600 mx-auto" />
            <p className="mt-3 font-semibold text-slate-300">{statusFilter ? `No ${statusFilter} quotes` : "No quotes saved yet"}</p>
            <p className="text-sm text-slate-500 font-zh">尚无报价记录</p>
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="quotes-list">
              {visible.map((qt) => (
                <QuoteRow key={qt.id} qt={qt} sym={sym} shop={shop} onPrint={print} onPdf={exportPdf} onSharePdf={sharePdf} open={open === qt.id} onToggle={() => setOpen(open === qt.id ? null : qt.id)} onDelete={() => remove(qt.id)} onStatus={(s) => setStatus(qt.id, s)} onOngoing={(v, opts) => setOngoing(qt.id, v, opts)} onInvoice={() => createInvoice(qt)} />
              ))}
            </div>
            <Pager page={current} pageCount={pageCount} total={filtered.length} onChange={changePage} testid="quotes-pager" />
          </>
        )}
      </div>
      {portal}
      {collectionPrompt}
    </div>
  );
}
